import { useCallback, useMemo } from 'react';

import useWorkspaceStore from '@/stores/WorkspaceStore';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

type Serializer<T> = (object: T) => string;
type Parser<T> = (val: string) => T | undefined;

const useWorkspaceStorage = <T>(
  key: string,
  initialValue: T,
  options?: {
    parser?: Parser<T>;
    serializer?: Serializer<T>;
  },
): [T, (value: T) => void, () => void] => {
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const workspace_id = currWorkspace?.alias;
  if (!workspace_id) throw new Error('Workspace ID is required');
  const workspaceStorage = useWorkspaceStore((state) => state.workspaceStorage);
  const setState = useWorkspaceStore((state) => state.setWorkspaceStorageItem);

  const state = useMemo(() => {
    return getWorkspaceStorageItem(key, initialValue, options?.parser);
  }, [workspaceStorage, key, initialValue, options?.parser]);

  const setStoreState = useCallback(
    (newValue: T) => {
      if (typeof newValue !== 'undefined') {
        setWorkspaceStorageItem(key, newValue, options?.serializer);
      } else {
        removeWorkspaceStorageItem(key);
      }
    },
    [key, options?.serializer],
  );

  const clearStorage = useCallback(() => {
    setState(key, initialValue);
    removeWorkspaceStorageItem(key);
  }, [key, initialValue, setState]);

  // 동기적으로 처리되므로 항상 초기화 완료
  return [state, setStoreState, clearStorage];
};

const getWorkspaceStorageItem = <T>(
  key: string,
  initialValue: T,
  parser?: Parser<T>,
): T => {
  try {
    const wa = getCurrWorkspaceAlias();
    if (!wa) return initialValue;
    const data = localStorage.getItem(`${key}_${wa}`);
    if (!data) return initialValue;
    return parser?.(data) || JSON.parse(data);
  } catch (e) {
    return initialValue;
  }
};

const setWorkspaceStorageItem = <T>(
  key: string,
  value: T,
  serializer?: Serializer<T>,
) => {
  const wa = useWorkspaceStore.getState().currWorkspace?.alias;
  if (!wa) return;
  useWorkspaceStore
    .getState()
    .setWorkspaceStorageItem(
      `${key}_${wa}`,
      serializer?.(value) || JSON.stringify(value),
    );
  localStorage.setItem(
    `${key}_${wa}`,
    serializer?.(value) || JSON.stringify(value),
  );
};

const removeWorkspaceStorageItem = (key: string) => {
  const wa = useWorkspaceStore.getState().currWorkspace?.alias;
  if (!wa) return;
  localStorage.removeItem(`${key}_${wa}`);
};

export default useWorkspaceStorage;

export {
  getWorkspaceStorageItem,
  removeWorkspaceStorageItem,
  setWorkspaceStorageItem,
};
